import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../../shared/lib/supabaseClient';
import { useAuth } from '../../../auth/context/AuthContext';
import { toast } from 'react-hot-toast';
import NonTickerConfirmationDialog from './NonTickerConfirmationDialog';
import { BASE_URL } from '../../../../shared/lib/api';

interface Props {
  isOpen: boolean;
  accountId: string | null;
  onClose: () => void;
  onRefreshImpacted2?: (accountId: string, hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean }) => Promise<void> | void;
  autoCloseOnSuccess?: boolean;
  onSuccess?: (hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean; newTickers?: string[] }, warnings?: { message: string; invalid_tickers: string[] }) => void;
}

const DefaultCsvUploadModal: React.FC<Props> = ({ isOpen, accountId, onClose, onRefreshImpacted2, autoCloseOnSuccess, onSuccess }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  
  // Initialize timezone from sessionStorage if available
  const [timezone, setTimezone] = useState<string>(() => {
    if (typeof window === 'undefined') return 'America/Los_Angeles';
    const storageKey = `vester:portfolio:csvDraft:default:${accountId || 'new'}`;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.timezone || 'America/Los_Angeles';
      } catch {
        return 'America/Los_Angeles';
      }
    }
    return 'America/Los_Angeles';
  });
  
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showNonTickerDialog, setShowNonTickerDialog] = useState(false);
  const [invalidTickers, setInvalidTickers] = useState<string[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Common timezones with IANA identifiers
  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
    { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
    { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    const handler = (e: any) => {
      // If parent doesn't control isOpen, this gives a way to open it globally.
      // This component is controlled, so we just no-op here; parent should set isOpen.
    };
    window.addEventListener('open-default-csv-upload', handler as any);
    return () => window.removeEventListener('open-default-csv-upload', handler as any);
  }, []);

  // Persist timezone to sessionStorage (debounced)
  useEffect(() => {
    if (typeof window === 'undefined' || !isOpen) return;
    const storageKey = `vester:portfolio:csvDraft:default:${accountId || 'new'}`;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ timezone }));
      } catch (e) {
        console.error('Failed to save timezone draft:', e);
      }
    }, 500); // 500ms debounce
    return () => clearTimeout(timer);
  }, [timezone, accountId, isOpen]);

  // Clear error message when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      setResult(null);
      setFile(null);
      setShowLoadingMessage(false);
    }
  }, [isOpen]);

  // Show loading message 3 seconds after upload starts
  useEffect(() => {
    if (busy) {
      const timer = setTimeout(() => {
        setShowLoadingMessage(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setShowLoadingMessage(false);
    }
  }, [busy]);

  const upload = async () => {
    if (!file || !accountId) return;
    setBusy(true);
    setResult(null);
    setErrorMessage(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const form = new FormData();
      form.append('file', file);
      form.append('account_id', accountId);
      form.append('broker', 'default');
      form.append('timezone', timezone);
      const res = await fetch(`${BASE_URL}/csv/upload`, { 
        method: 'POST', 
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const json = await res.json();
      if (!autoCloseOnSuccess) {
        setResult(json);
      }
      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(file);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        const hints = json?.impacted || json?.recalculate?.hints;

        if (typeof window !== 'undefined' && accountId) {
          const storageKey = `vester:portfolio:csvDraft:default:${accountId}`;
          sessionStorage.removeItem(storageKey);
        }

        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
          if (accountId) {
            queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
          }
        }

        if (accountId && hints && onRefreshImpacted2) {
          try {
            await onRefreshImpacted2(accountId, {
              tickers: hints.tickers || [],
              dividendTickers: hints.dividendTickers || hints.div_changed || [],
              cash_changed: !!hints.cash_changed,
            });
          } catch (e) {
            console.error('refreshImpactedData2 error', e);
          }
        }
        if (onSuccess) {
          try {
            const newTickers = json?.new_tickers ?? [];
            onSuccess({
              tickers: hints?.tickers || [],
              dividendTickers: hints?.dividendTickers || hints?.div_changed || [],
              cash_changed: !!hints?.cash_changed,
              ...(newTickers.length > 0 && { newTickers }),
            }, json?.warnings);
          } catch {}
        }
        if (autoCloseOnSuccess) {
          setResult(null);
          onClose();
        }
      } else {
        if (json?.error_code === 'CSV_INVALID_TICKERS' && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(file);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        
        setErrorMessage('Error uploading CSV.');
        toast.error(json?.error || 'Upload failed');
      }
    } catch (e) {
      setResult({ error: String(e) });
      setErrorMessage('Error uploading CSV.');
      toast.error(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleFileInputClick = () => {
    setIsFilePickerOpen(true);
    // Temporarily reduce z-index to allow file picker to appear on top
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    setIsFilePickerOpen(false);
  };

  // Handle non-ticker confirmation: re-upload with include_tickers so backend creates MANUAL + history in Phase 0
  const handleNonTickerConfirm = async () => {
    if (!invalidTickers.length || !pendingFile || !accountId) return;

    try {
      setShowNonTickerDialog(false);
      setBusy(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No auth token');

      const form = new FormData();
      form.append('file', pendingFile);
      form.append('account_id', accountId);
      form.append('broker', 'default');
      form.append('timezone', timezone);
      form.append('include_tickers', invalidTickers.join(','));

      const res = await fetch(`${BASE_URL}/csv/upload`, {
        method: 'POST',
        body: form,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();

      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        const hints = json?.impacted || json?.recalculate?.hints;
        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
          queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
        }
        if (accountId && hints && onRefreshImpacted2) {
          try {
            await onRefreshImpacted2(accountId, {
              tickers: hints.tickers || [],
              dividendTickers: hints.dividendTickers || hints.div_changed || [],
              cash_changed: !!hints.cash_changed,
            });
          } catch (e) {
            console.error('refreshImpactedData2 error', e);
          }
        }
        if (hints && onSuccess) {
          try {
            const newTickers = json?.new_tickers ?? [];
            onSuccess({
              tickers: hints.tickers || [],
              dividendTickers: hints.dividendTickers || hints.div_changed || [],
              cash_changed: !!hints.cash_changed,
              ...(newTickers.length > 0 && { newTickers }),
            }, json?.warnings);
          } catch {}
        }
        if (autoCloseOnSuccess) {
          setResult(null);
          onClose();
        }
      } else {
        setErrorMessage('Error uploading CSV after adding custom tickers.');
        toast.error(json?.error || 'Upload failed');
      }
    } catch (error: unknown) {
      const err = error instanceof Error ? error.message : 'Failed to add custom tickers. Please try again.';
      setErrorMessage(err);
      toast.error(err);
    } finally {
      setBusy(false);
      setInvalidTickers([]);
      setPendingFile(null);
    }
  };

  // Handle non-ticker dialog cancel
  const handleNonTickerCancel = () => {
    setShowNonTickerDialog(false);
    setInvalidTickers([]);
    setPendingFile(null);
    setBusy(false);
  };

  // Handle exclude from upload: retry upload with excluded tickers
  const handleExcludeFromUpload = async () => {
    if (!invalidTickers.length || !pendingFile) return;

    try {
      setShowNonTickerDialog(false);
      setBusy(true);

      // Retry the CSV upload with excluded tickers
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No auth token');
      }

      const form = new FormData();
      form.append('file', pendingFile);
      form.append('account_id', accountId!);
      form.append('broker', 'default');
      form.append('timezone', timezone);
      form.append('exclude_tickers', invalidTickers.join(',')); // Send excluded tickers to backend
      
      const res = await fetch(`${BASE_URL}/csv/upload`, { 
        method: 'POST', 
        body: form,
        headers: session.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      
      const json = await res.json();

      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        const hints = json?.impacted || json?.recalculate?.hints;
        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
          if (accountId) {
            queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
          }
        }
        if (accountId && hints && onRefreshImpacted2) {
          try {
            await onRefreshImpacted2(accountId, {
              tickers: hints.tickers || [],
              dividendTickers: hints.dividendTickers || hints.div_changed || [],
              cash_changed: !!hints.cash_changed,
            });
          } catch (e) {
            console.error('refreshImpactedData2 error', e);
          }
        }
        if (hints && onSuccess) {
          try {
            const newTickers = json?.new_tickers ?? [];
            onSuccess({
              tickers: hints.tickers || [],
              dividendTickers: hints.dividendTickers || hints.div_changed || [],
              cash_changed: !!hints.cash_changed,
              ...(newTickers.length > 0 && { newTickers }),
            }, json?.warnings);
          } catch {}
        }
        if (autoCloseOnSuccess) {
          setResult(null);
          onClose();
        }
      } else {
        if (json?.error_code === 'CSV_INVALID_TICKERS' && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        setErrorMessage('Error uploading CSV after excluding tickers.');
        toast.error(json?.error || 'Upload failed');
      }
    } catch (error: unknown) {
      const err = error instanceof Error ? error.message : 'Failed to upload CSV. Please try again.';
      setErrorMessage(err);
      toast.error('Failed to upload CSV. Please try again.');
    } finally {
      setBusy(false);
      setInvalidTickers([]);
      setPendingFile(null);
    }
  };

  if (!isOpen) return null;
  return (
    <div className={`fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-black bg-opacity-30 ${isFilePickerOpen ? 'z-[100]' : 'z-[10000]'}`}>
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Upload Default CSV</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        {errorMessage && (
          <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
            {errorMessage}
          </div>
        )}
        <div className="space-y-3">
          <input 
            ref={fileInputRef}
            type="file" 
            accept=".csv" 
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <div className="flex items-end gap-3">
            <button
              onClick={handleFileInputClick}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Choose CSV File
            </button>
            
            {/* Timezone Dropdown */}
            <div className="flex-1">
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                Timezone for Date Parsing
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {file && (
            <div className="text-sm text-gray-600">
              Selected: {file.name}
            </div>
          )}
          <p className="text-xs text-gray-500">
            Dates without timezone info will be interpreted in the selected timezone (defaults to PST).
          </p>

          <a
            href="https://docs.google.com/spreadsheets/d/1vtgCHLQ4g_eQ-zv3Nx5ZAudO8zs260moeeCHoe-4h34/edit?gid=0#gid=0"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 block mb-2"
          >
            See example CSV in Google Sheets
          </a>

          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showHelp ? 'Hide' : 'Show'} file format help
          </button>
          {showHelp && (
            <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 space-y-2 max-h-64 overflow-y-auto">
              <p className="font-semibold mb-2">CSV Specifications: </p>
              <p className="mb-2">Headers: Datetime, TransactionType, Ticker, OptionsInfo, Quantity, Price, Commission, TotalAmount, Notes</p>
              
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 px-2 font-semibold">Header</th>
                      <th className="text-left py-2 px-2 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Datetime*</td>
                      <td className="py-2 px-2">Date and time (MM/DD/YYYY HH:mm:ss recommended, flexible parsing). Select timezone from dropdown (Default = PST). If no time is provided, defaults to 16:59:59 UTC (~12:59:59 ET during daylight saving)</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">TransactionType*</td>
                      <td className="py-2 px-2">Accepted entries: Buy, Sell, Short Sell, Buy to Cover, Call, Put, Call Sell, Put Sell, Dividend, Dividend in Lieu, Deposit, Withdrawal, Interest, Adjustment</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Ticker</td>
                      <td className="py-2 px-2">For equity/dividend transactions (e.g., AAPL)</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">OptionsInfo</td>
                      <td className="py-2 px-2">For options only - format "&lt;Symbol&gt; &lt;Expiration Date&gt; &lt;Strike price&gt;" (e.g., "GM Aug 08 '25 $51")</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Quantity</td>
                      <td className="py-2 px-2">Shares for equity, # of contracts for options</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Price</td>
                      <td className="py-2 px-2">Price per share for equity, premium per contract for options</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Commission</td>
                      <td className="py-2 px-2">Transaction fee</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">TotalAmount</td>
                      <td className="py-2 px-2">Total transaction value (can be calculated if not provided)</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 font-medium">Notes</td>
                      <td className="py-2 px-2">User notes</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <p className="font-medium mb-1">Sign Conventions:</p>
                <ul className="list-disc list-inside ml-2 space-y-1 text-xs">
                  <li>Deposits Dividends, and Interest: Total amount always positive</li>
                  <li>Withdrawals and Dividend in Lieu: Total amount always negative</li>
                  <li>Adjustment: Total amount can be positive or negative</li>
                  <li>Equity/Options: Total amount negative for a Buy/Buy to Cover, positive for a Sell/Short Sell</li>
                </ul>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button
              onClick={upload}
              disabled={!file || !accountId || busy}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {busy && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {busy ? 'Uploading...' : 'Upload'}
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
            {showLoadingMessage && (
              <span className="text-sm text-gray-600 ml-2">This may take a second ...</span>
            )}
          </div>
          {result && (
            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-64">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      </div>

      {/* Non-Ticker Confirmation Dialog */}
      <NonTickerConfirmationDialog
        isOpen={showNonTickerDialog}
        tickers={invalidTickers}
        onConfirm={handleNonTickerConfirm}
        onCancel={handleNonTickerCancel}
        onExclude={handleExcludeFromUpload}
      />
    </div>
  );
};

export default DefaultCsvUploadModal;

